/**
 * Sticky mobile PDP header: expand when <variant-picker> intersects the viewport.
 * data-thumb-anim=true only on intersection *changes* so padding/img transitions
 * do not run on variant-only DOM updates.
 */
const ROOT_MARGIN = '-96px 0px 0px 0px';
const VIEW_TOP = 96;
const ANIM_MS = 380;
const REBIND_MS = [48, 160, 300];

/** @type {Map<string, { obs: IntersectionObserver; prev?: boolean }>} */
const bySection = new Map();

function pickerInView(el) {
  const r = el.getBoundingClientRect();
  return !(r.bottom <= VIEW_TOP || r.top >= innerHeight || r.right <= 0 || r.left >= innerWidth);
}

function thumbAnimOff(header) {
  clearTimeout(header._taf);
  header._taf = setTimeout(() => {
    header.dataset.thumbAnim = 'false';
    header._taf = null;
  }, ANIM_MS);
}

function setExpanded(header, open, animate) {
  const next = open ? 'true' : 'false';
  if (header.getAttribute('data-expanded') === next) return;
  if (animate) {
    header.dataset.thumbAnim = 'true';
    requestAnimationFrame(() => {
      header.setAttribute('data-expanded', next);
      thumbAnimOff(header);
    });
  } else {
    header.dataset.thumbAnim = 'false';
    header.setAttribute('data-expanded', next);
  }
}

function syncGeometry(header) {
  const sid = header.dataset.sectionId;
  const main = sid && document.getElementById(sid);
  const picker = main?.querySelector('variant-picker');
  if (!picker) return;
  setExpanded(header, pickerInView(picker), false);
}

function disconnect(sid) {
  const row = bySection.get(sid);
  if (row) {
    row.obs.disconnect();
    bySection.delete(sid);
  }
}

function bind(header) {
  const sid = header.dataset.sectionId;
  if (!sid) return;

  clearTimeout(header._taf);
  header.dataset.thumbAnim = 'false';
  disconnect(sid);

  const main = document.getElementById(sid);
  const picker = main?.querySelector('variant-picker');
  if (!picker) return;

  const row = { obs: null, prev: undefined };
  row.obs = new IntersectionObserver(
    (entries) => {
      const on = entries.some((e) => e.isIntersecting);
      const want = on ? 'true' : 'false';
      if (header.getAttribute('data-expanded') === want) {
        row.prev = on;
        return;
      }
      const animate = row.prev !== undefined && row.prev !== on;
      setExpanded(header, on, animate);
      row.prev = on;
    },
    { rootMargin: ROOT_MARGIN, threshold: 0 }
  );
  row.obs.observe(picker);
  bySection.set(sid, row);

  syncGeometry(header);
}

function init() {
  const headers = [...document.querySelectorAll('[data-sticky-mobile-pdp-header]')];
  for (const h of headers) {
    clearTimeout(h._taf);
    h.dataset.thumbAnim = 'false';
  }

  const seen = new Set();
  for (const h of headers) {
    const sid = h.dataset.sectionId;
    if (!sid) continue;
    seen.add(sid);
    bind(h);
  }
  for (const sid of [...bySection.keys()]) {
    if (!seen.has(sid)) disconnect(sid);
  }
}

function fromMainProductPicker(e) {
  return (e.composedPath?.() ?? []).some(
    (n) => n instanceof HTMLElement && n.tagName.toUpperCase() === 'MAIN-PRODUCT'
  );
}

let rebindT;
function scheduleRebind() {
  clearTimeout(rebindT);
  const run = () => init();
  queueMicrotask(run);
  requestAnimationFrame(() => requestAnimationFrame(run));
  for (const ms of REBIND_MS) setTimeout(run, ms);
  rebindT = setTimeout(run, 360);
}

let attached;
function boot() {
  if (!attached) {
    attached = true;
    document.addEventListener(
      'variant-picker:change',
      (e) => {
        if (fromMainProductPicker(e)) scheduleRebind();
      },
      true
    );
    window.addEventListener(
      'pagehide',
      () => {
        clearTimeout(rebindT);
        for (const sid of [...bySection.keys()]) disconnect(sid);
      },
      { once: true }
    );
  }
  init();
}

document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', boot, { once: true })
  : boot();
